/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, { IImageData, Image, IRequestContext } from '@msdyn365-commerce/core';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import React from 'react';

type AreaEvent = Event | React.MouseEvent<HTMLAreaElement, MouseEvent> | React.FocusEvent<HTMLAreaElement>;

export interface IRgbColor {
    rValue: number;
    gValue: number;
    bValue: number;
}

export interface IArea {
    areaId: string;
    coords: number[];
    center?: number[];
    primaryImageUrl?: string;
    productUrl: string;
    productSearchResult?: ProductSearchResult;
}
export interface IAreaMap {
    name: string;
    areas: IArea[];
}

export enum CanvasCalculations {
    defaultWidth = 0,
    defaultHeight = 0,
    defaultPositionX = 0,
    defaultPositionY = 0,
    defaultScale = 1,
    halfDivisor = 2,
    doubleMultiplier = 2,
    buttonHeight = 30,
    buttonPadding = 30,
    paddingAdjustment = 3,
    expandAnimationStartWidth = 10,
    expandAnimationEndWidth = 40,
    collapseAnimationStartWidth = 40,
    collapseAnimationEndWidth = 8,
    expandRadius = 12,
    collapseRadius = 8,
    rotation = 1.57,
    startAngle = 0,
    endAngle = 7,
    clearMargin = 50,
    clearHeight = 100,
    clearWidth = 100,
    textFadeInStartValue = 0,
    textFadeOutStartValue = 0.5,
    textFadeInterval = 0.01,
    innerPadding = 0.4,
    strokeRatio = 0.15
}

export enum CanvasStyles {
    black = 'rgba(30, 30, 30, 0.95)',
    white = 'rgba(235, 235, 235, 0.95)'
}

export interface IImageMapperResources {
    buttonText?: string;
    altText?: string;
}

export interface IImageMapperProps {
    imageData: IImageData;
    areaMap: IAreaMap;
    activeArea?: IArea;
    viewPort: string;
    requestContext: IRequestContext;
    color?: string;
    buttonTextClor?: string;
    width?: number;
    imgWidth?: number;
    activeImageRefs: React.RefObject<HTMLDivElement>;
    imageMapperResources?: IImageMapperResources;
    onLoad?(): void;
    onClick?(area: IArea, event: AreaEvent): void;
    onFocus?(area: IArea, event: AreaEvent): void;
    onMouseEnter?(area: IArea, index: number, event: AreaEvent): void;
    onMouseLeave?(area: IArea, index: number, event: AreaEvent): void;
}
export class ImageMapper extends React.Component<IImageMapperProps> {
    private readonly _backgroundImage: React.RefObject<Image>;

    private readonly _container: React.RefObject<HTMLDivElement>;

    private readonly _canvas: React.RefObject<HTMLCanvasElement>;

    private _context: CanvasRenderingContext2D | null | undefined = null;

    public constructor(props: IImageMapperProps) {
        super(props);
        this._backgroundImage = React.createRef();
        this._container = React.createRef();
        this._canvas = React.createRef();
    }

    /**
     * ComponentDidMount method.
     */
    public componentDidMount(): void {
        this.initCanvas();
        this.renderAreas();
        this._context = null;
    }

    /**
     * ComponentDidUpdate method.
     */
    public componentDidUpdate(): void {
        this.initCanvas();
    }

    /**
     * ShouldComponentUpdate method.
     * @param nextProps - Next props.
     * @returns Returns boolean.
     */
    public shouldComponentUpdate(nextProps: IImageMapperProps): boolean {
        let shouldRender = false;
        if (nextProps !== this.props) {
            shouldRender = true;
        }
        return shouldRender;
    }

    /**
     * GetActiveImageWidth method - Gets the active image width.
     * @returns Returns number | undefined.
     */
    public getActiveImageWidth = (): number | undefined => {
        if (MsDyn365.isBrowser) {
            const activeImageReferenceValue = this.props.activeImageRefs;
            if (activeImageReferenceValue.current !== null) {
                return activeImageReferenceValue.current.clientWidth;
            }
        }
        return undefined;
    };

    /**
     * GetActiveImageHeight method - Gets the active image height.
     * @returns Returns number | undefined.
     */
    public getActiveImageHeight = (): number | undefined => {
        if (MsDyn365.isBrowser) {
            const activeImageReferenceValue = this.props.activeImageRefs;
            if (activeImageReferenceValue.current !== null) {
                return activeImageReferenceValue.current.clientHeight;
            }
        }
        return undefined;
    };

    private drawMinus = (coords: number[], color: string): void => {
        const [x, y, radius] = coords;
        const context = this._canvas.current?.getContext('2d') ?? this._context;
        if (!context) {
            return;
        }
        const length = radius - radius * CanvasCalculations.innerPadding;
        context.strokeStyle = color;
        context.lineWidth = CanvasCalculations.strokeRatio * radius;
        context.beginPath();
        context.moveTo(x - length, y);
        context.lineTo(x + length, y);
        context.stroke();
    };

    private drawPlus = (coords: number[], color: string): void => {
        const [x, y, radius] = coords;
        const context = this._canvas.current?.getContext('2d') ?? this._context;
        if (!context) {
            return;
        }
        const length = radius - radius * CanvasCalculations.innerPadding;
        context.strokeStyle = color;
        context.lineWidth = CanvasCalculations.strokeRatio * radius;
        context.beginPath();
        context.moveTo(x - length, y);
        context.lineTo(x + length, y);
        context.stroke();
        context.beginPath();
        context.moveTo(x, y - length);
        context.lineTo(x, y + length);
        context.stroke();
    };

    /**
     * DrawCircle method - Renders active points.
     * @param coords -Coordinates.
     */
    public drawCircle = (coords: number[], color: string): void => {
        const doubleMultiplier: number = CanvasCalculations.doubleMultiplier;
        const startAngle: number = CanvasCalculations.startAngle;
        const context = this._canvas.current?.getContext('2d') ?? this._context;
        const radius = coords[2];
        if (!context) {
            return;
        }
        context.fillStyle = color;
        context.beginPath();
        context.strokeStyle = color;
        context.arc(coords[0], coords[1], radius, startAngle, Math.PI * doubleMultiplier);
        context.closePath();
        context.stroke();
        context.fill();
    };

    /**
     * ScaleCoords method - To scale the active point coordinates based on the window size.
     * @param coords -Coordinates.
     * @returns -Returns number[].
     */
    public scaleCoords = (coords: number[]): number[] => {
        const { imgWidth } = this.props;
        const defaultWidth: number = CanvasCalculations.defaultWidth;
        const defaultScale: number = CanvasCalculations.defaultScale;

        // Calculate scale based on current 'width' and the original 'imgWidth'
        const width = this.getActiveImageWidth();
        const scale = width && imgWidth && imgWidth > defaultWidth ? width / imgWidth : defaultScale;
        return [coords[0] * scale, coords[1] * scale, coords[2]];
    };

    /**
     * RenderPrefilledAreas method - To render active points in case of events.
     */
    public renderPrefilledAreas = (): void => {
        if (this._canvas.current) {
            const context = this._canvas.current.getContext('2d') ?? this._context;
            if (!context) {
                return;
            }
            const canvasPositionX: number = CanvasCalculations.defaultPositionX;
            const canvasPositionY: number = CanvasCalculations.defaultPositionY;
            context.clearRect(canvasPositionX, canvasPositionY, this._canvas.current.width, this._canvas.current.height);
            const { areaMap, activeArea } = this.props;
            areaMap.areas.forEach(area => {
                const scaledCoords = this.scaleCoords(area.coords);
                if (activeArea && area.coords[0] === activeArea.coords[0] && area.coords[1] === activeArea.coords[1]) {
                    this.drawCircle(scaledCoords, CanvasStyles.black);
                    this.drawMinus(scaledCoords, CanvasStyles.white);
                } else {
                    this.drawCircle(scaledCoords, CanvasStyles.white);
                    this.drawPlus(scaledCoords, CanvasStyles.black);
                }
            });
        }
    };

    /**
     * ComputeCenter method - To re-calculate the center of active point based on window size.
     * @param area -IArea.
     * @returns -Returns number[].
     */
    public computeCenter = (area: IArea): number[] => {
        const scaledCoords = this.scaleCoords(area.coords);
        return [scaledCoords[0], scaledCoords[1]];
    };

    /**
     * HoverOn method - Mouse hover on event.
     * @param area -IArea.
     * @param index - Number.
     * @returns - Void.
     */
    public hoverOn = (area: IArea, index: number) => (event: AreaEvent): void => {
        const { onMouseEnter } = this.props;
        onMouseEnter && onMouseEnter(area, index, event);
    };

    /**
     * HoverOff method - Mouse hover off event.
     * @param area - IArea.
     * @param index - Number.
     * @returns - Void.
     */
    public hoverOff = (area: IArea, index: number) => (event: AreaEvent): void => {
        const { onMouseLeave } = this.props;
        onMouseLeave && onMouseLeave(area, index, event);
    };

    /**
     * RenderAreas method - Renders active points at given coordinates.
     * @returns -Returns JSX.Element[].
     */
    public renderAreas = (): JSX.Element[] => {
        const { areaMap } = this.props;
        const buttonText = this.props.imageMapperResources?.buttonText ? this.props.imageMapperResources.buttonText : '';
        const altText = this.props.imageMapperResources?.altText ? this.props.imageMapperResources.altText : '';

        return areaMap.areas.map((area, index) => {
            const scaledCoords = this.scaleCoords(area.coords);
            const center = this.computeCenter(area);
            const extendedArea = { ...area, scaledCoords, center };
            const areaKey = `area-${index}`;
            const altTextWithProductName = altText
                .replace('{buttonText}', buttonText ? buttonText : '')
                .replace('{productName}', area.productSearchResult?.Name ? area.productSearchResult.Name : '');
            return (
                <area
                    key={areaKey}
                    shape='circle'
                    coords={scaledCoords.join(',')}
                    onMouseEnter={this.hoverOn(extendedArea, index)}
                    onMouseLeave={this.hoverOff(extendedArea, index)}
                    onFocus={this.hoverOn(extendedArea, index)}
                    onBlur={this.hoverOff(extendedArea, index)}
                    tabIndex={0}
                    alt={altTextWithProductName}
                    aria-label={altTextWithProductName}
                />
            );
        });
    };

    /**
     * InitCanvas method - To render the canvas.
     */
    public initCanvas = (): void => {
        if (!this._canvas.current || !this._backgroundImage.current || !this._container.current) {
            return;
        }

        const { color, onLoad } = this.props;
        const defaultWidth: number = CanvasCalculations.defaultWidth;
        const defaultHeight: number = CanvasCalculations.defaultHeight;

        const canvasWidth = this.getActiveImageWidth() ?? defaultWidth;
        const canvasHeight = this.getActiveImageHeight() ?? defaultHeight;

        this._canvas.current.width = canvasWidth;
        this._canvas.current.height = canvasHeight;
        this._container.current.style.width = `${canvasWidth}px`;

        const context = this._canvas.current.getContext('2d') ?? this._context;
        if (!context) {
            return;
        }
        context.fillStyle = color ?? '';

        if (onLoad) {
            onLoad();
        }

        this.renderPrefilledAreas();
    };

    /**
     * Render method - To render the image mapper component with active image, canvas and active points.
     * @returns -Returns JSX.Element | null.
     */
    public render(): JSX.Element | null {
        const { imageData, areaMap, requestContext } = this.props;

        return (
            <div className='msc-active-image-container' ref={this._container}>
                <Image
                    src={imageData.src}
                    useMap={`#${areaMap.name}`}
                    alt={areaMap.name}
                    ref={this._backgroundImage}
                    height={imageData.height}
                    width={imageData.width}
                    className='msc-active-image-img'
                    imageSettings={imageData.imageSettings}
                    gridSettings={requestContext.gridSettings ?? {}}
                    requestContext={requestContext}
                />
                <canvas ref={this._canvas} className='msc-active-image-canvas' />
                <map name={areaMap.name} className='msc-active-image-map'>
                    {this.renderAreas()}
                </map>
            </div>
        );
    }
}

export default ImageMapper;
